import type { FC, ReactNode } from 'react';
import { Suspense, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import VerticalEllipsisIcon from '@wix/da-ds/pkg/Icons/16x16/VerticalEllipsis';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import GiftingReduxModuleLoaderLazy from '@wix/da-shared-react/pkg/gifting/redux/ModuleLoaderLazy';
import { initGiftState } from '@wix/da-shared-react/pkg/gifting/redux/actions';

import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import useContextMenuItems from './useContextMenuItems';
import { useAction } from '@wix/da-hooks/pkg/useAction';

interface Props {
  className?: string;
  wrapperClassName?: string;
  expandedActions?: boolean;
  user: PapiUser;
}

const TopSubscribersActionMenu: FC<Props> = ({
  className,
  wrapperClassName,
  expandedActions = false,
  user,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const items = useContextMenuItems(user, expandedActions);
  const dispatchInitGiftState = useAction(initGiftState);

  const handleOpen = useCallback(() => {
    dispatchInitGiftState(user);
  }, [dispatchInitGiftState, user]);

  return (
    (<div className={wrapperClassName}>
      {/* We need this wrapper div to avoid a layout shift on render */}
      {expandedActions ? (
        (items as ReactNode[])
      ) : (
        <IconButtonWithContextMenu
          className={className}
          placement={'bottom-start'}
          items={items as NullableDropdownItem[]}
          size="medium"
          popperManagerProps={{
            onOpen: handleOpen,
          }}
          variant="main"
          icon={isMobile ? VerticalEllipsisIcon : EllipsisIcon}
          aria-label={t('siteHeader.more')}
          onClick={e => e.stopPropagation()}
          title={t('common.more')}
          standardMenuProps={{ usePortal: false } as any}
        />
      )}
      <Suspense fallback={null}>
        <GiftingReduxModuleLoaderLazy />
      </Suspense>
    </div>)
  );
};

TopSubscribersActionMenu.displayName = 'TopSubscribersActionMenu';

export default TopSubscribersActionMenu;
